const DEFAULT_HEADER = { "Content-Type": "application/json" }

class ApiClient {
  constructor() {
    this.cache = {}
    this.pendingRequests = {}
  }

  async handleFetchError(response) {
    const error = new Error("Something went wrong. Please try again.")

    // Api is successful, but server is sending some descriptive error (response.resultInfo.message), so throw that error then
    if (response?.resultInfo) {
      error.message =
        response.resultInfo?.message ||
        "Something went wrong. Please try again."
      error.status = response.resultInfo?.statusCode
      error.statusText = response.resultInfo?.status
    } else {
      const errResponseJson = await response.json()

      // If server is sending error, then it will have error message in info key or resultInfo
      error.message =
        typeof errResponseJson?.info === "string"
          ? errResponseJson.info
          : errResponseJson?.info?.resultInfo?.message ||
            "Something went wrong. Please try again."
      error.status = response.status
      error.statusText = response.statusText
    }

    throw error
  }

  async get(endpoint, config = {}) {
    const { useCache = false, params = {}, headers = {} } = config
    const cacheKey = endpoint + JSON.stringify(params)

    if (useCache && this.cache[cacheKey]) {
      return this.cache[cacheKey]
    }

    if (this.pendingRequests[cacheKey]) {
      // Return the resolved JSON data from the pending request
      return await this.pendingRequests[cacheKey]
    }

    // Construct query string from params
    const queryString = new URLSearchParams(params).toString()
    const url = queryString ? `${endpoint}?${queryString}` : endpoint

    // Define the fetch request function
    const fetchRequest = async () => {
      const response = await fetch(url, {
        method: "GET",
        headers: new Headers(headers),
      })

      if (!response.ok) {
        await this.handleFetchError(response)
      }

      const responseData = await response.json()

      if (responseData.resultInfo?.status !== "SUCCESS") {
        await this.handleFetchError(responseData)
      }

      // Cache the resolved data if caching is enabled
      if (useCache) {
        this.cache[cacheKey] = responseData
      }

      return responseData
    }

    // Store the promise from the fetch request
    this.pendingRequests[cacheKey] = fetchRequest()

    // Await the pending request and return resolved JSON data
    try {
      const responseData = await this.pendingRequests[cacheKey]
      delete this.pendingRequests[cacheKey] // Cleanup after success
      return responseData
    } catch (error) {
      delete this.pendingRequests[cacheKey] // Cleanup after error
      throw error
    }
  }

  // Although we would never need caching for POST Api, but there is one api (bulk config to fetch widget configuration), that needs caching all the time.
  // So caching logic for POST Api was implemented also similar to GET Api
  async post(endpoint, data, config = {}) {
    const { useCache = false, headers = {} } = config

    const isFormData = data instanceof FormData

    // This is being done only because JSON.stringify is heavy operation, and we will rarely need the POST Api caching, so no need to complex the logic
    const cacheKey =
      useCache && !isFormData
        ? endpoint + JSON.stringify(data)
        : endpoint + "tempCacheKey"

    if (useCache && !isFormData && this.cache[cacheKey]) {
      return this.cache[cacheKey]
    }

    if (this.pendingRequests[cacheKey]) {
      // Return the resolved JSON data from the pending request
      return await this.pendingRequests[cacheKey]
    }

    // When we use this method to upload anything (multipart/form-data), then we shouldn't set any headers. The browser handles this and includes the correct headers on its own.
    const requestHeader = isFormData
      ? new Headers(headers)
      : new Headers({
          ...DEFAULT_HEADER,
          ...headers,
        })

    const fetchRequest = async () => {
      const response = await fetch(endpoint, {
        method: "POST",
        headers: requestHeader,
        body: isFormData ? data : JSON.stringify(data),
      })

      if (!response.ok) {
        await this.handleFetchError(response)
      }

      const responseData = await response.json()

      if (responseData.resultInfo?.status !== "SUCCESS") {
        await this.handleFetchError(responseData)
      }

      if (useCache && !isFormData) {
        this.cache[cacheKey] = responseData
      }

      return responseData
    }

    // Store the promise from the fetch request
    this.pendingRequests[cacheKey] = fetchRequest()

    try {
      const responseData = await this.pendingRequests[cacheKey]
      delete this.pendingRequests[cacheKey] // Cleanup after success
      return responseData
    } catch (error) {
      delete this.pendingRequests[cacheKey] // Cleanup after error
      throw error
    }
  }

  async put(endpoint, data, config = {}) {
    const { headers = {} } = config

    const isFormData = data instanceof FormData

    // When we use this method to upload anything (multipart/form-data), then we shouldn't set any headers. The browser handles this and includes the correct headers on its own.
    const requestHeader = isFormData
      ? new Headers(headers)
      : new Headers({
          ...DEFAULT_HEADER,
          ...headers,
        })

    const response = await fetch(endpoint, {
      method: "PUT",
      headers: requestHeader,
      body: isFormData ? data : JSON.stringify(data),
    })

    if (!response.ok) {
      await this.handleFetchError(response)
    }

    const responseData = await response.json()

    if (responseData.resultInfo?.status !== "SUCCESS") {
      return this.handleFetchError(responseData)
    }

    return responseData
  }

  async delete(endpoint, config = {}) {
    const { headers = {} } = config

    const response = await fetch(endpoint, {
      method: "DELETE",
      headers: new Headers(headers),
    })

    if (!response.ok) {
      await this.handleFetchError(response)
    }

    const responseData = await response.json()

    if (responseData.resultInfo?.status !== "SUCCESS") {
      return this.handleFetchError(responseData)
    }

    return responseData
  }

  clearCache(endpoint, exclude = false) {
    const shouldExclude = exclude && endpoint

    // if exclude is false, clear all cache except for the specified endpoint
    // if exclude is true, clear cache for the specified endpoint
    Object.keys(this.cache).forEach((key) => {
      if (
        (shouldExclude && !key.startsWith(endpoint)) ||
        (!shouldExclude && key.startsWith(endpoint))
      ) {
        delete this.cache[key]
      }
    })

    // Clear cache completely
    if (!endpoint) {
      this.cache = {}
    }
  }
}

const apiClient = new ApiClient()

export default apiClient
